import type {
    MediaDeviceInfoLike,
    DeviceOrTrack,
    MediaStreamTrackLike,
} from './types';
import {MediaDeviceFailure} from './types';
import {toKey, type DeviceKey} from './devices';
import {isMediaStreamTrack} from './typeGuards';

export const createStreamTrackMap = (tracks?: MediaStreamTrackLike[]) => {
    const streamTrackMap = new Map<DeviceKey, MediaStreamTrackLike>(
        tracks?.map(track => [toKey(track), track]),
    );
    type DeviceIndeKey = string & {__type: 'device-index-key'};

    const toDeviceIndexKey = (
        track: MediaStreamTrackLike,
    ): DeviceIndeKey | undefined => {
        const {deviceId} = track.getSettings();
        return deviceId
            ? (`${track.kind}:${deviceId}` as DeviceIndeKey)
            : undefined;
    };

    const toDeviceQueryKey = (
        device: MediaDeviceInfoLike,
    ): DeviceIndeKey | undefined => {
        switch (device.kind) {
            case 'audioinput':
                return `audio:${device.deviceId}` as DeviceIndeKey;
            case 'videoinput':
                return `video:${device.deviceId}` as DeviceIndeKey;
            default:
                return undefined;
        }
    };

    /**
     * Secondary index: `${track.kind}:${deviceId}` → tracks for O(1) device lookups.
     * Uses a Set per key so multiple tracks sharing a deviceId stay findable
     * until every one of them is removed.
     */
    const deviceIndex = new Map<DeviceIndeKey, Set<MediaStreamTrackLike>>();

    const addToDeviceIndex = (track: MediaStreamTrackLike) => {
        const indexKey = toDeviceIndexKey(track);
        if (!indexKey) {
            return;
        }
        const existing = deviceIndex.get(indexKey);
        if (existing) {
            existing.add(track);
        } else {
            deviceIndex.set(indexKey, new Set([track]));
        }
    };

    const removeFromDeviceIndex = (track: MediaStreamTrackLike) => {
        const indexKey = toDeviceIndexKey(track);
        if (!indexKey) {
            return;
        }
        const existing = deviceIndex.get(indexKey);
        if (!existing) {
            return;
        }
        existing.delete(track);
        if (existing.size === 0) {
            deviceIndex.delete(indexKey);
        }
    };

    for (const track of streamTrackMap.values()) {
        addToDeviceIndex(track);
    }

    /**
     * Find track from the list of track in use.
     */
    const findTrack = (
        device: MediaDeviceInfoLike,
    ): MediaStreamTrackLike | undefined => {
        const queryKey = toDeviceQueryKey(device);
        if (!queryKey) {
            return undefined;
        }
        const tracks = deviceIndex.get(queryKey);
        return tracks?.values().next().value;
    };

    /**
     * Construct a `DeviceKey` from `DeviceOrTrack`
     */
    const toStreamTrackKey = (deviceOrTrack: DeviceOrTrack): DeviceKey => {
        // Unify interfaces
        const track = isMediaStreamTrack(deviceOrTrack)
            ? deviceOrTrack
            : findTrack(deviceOrTrack as MediaDeviceInfoLike);
        const kind = track?.kind || 'unknown-kind';
        const id = track?.id || 'unknown-id';
        const label = track?.label || 'unknown-label';
        return toKey({kind, id, label});
    };

    /**
     * Add the `MediaStreamTrack` to `streamTrackMap`
     */
    const add = (track: MediaStreamTrackLike) => {
        const key = toStreamTrackKey(track);
        if (key) {
            addToDeviceIndex(track);
            return streamTrackMap.set(key, track);
        }
        throw new Error(MediaDeviceFailure.StreamTrackNotFound);
    };

    /**
     * Check if `streamTrackMap` has provided track or device
     */
    const has = (deviceOrTrack: DeviceOrTrack) => {
        const key = toStreamTrackKey(deviceOrTrack);
        return key && streamTrackMap.has(key);
    };

    /**
     * Stop all tracks and empty `streamTrackMap`
     */
    const clear = () => {
        for (const track of streamTrackMap.values()) {
            track.stop();
        }
        streamTrackMap.clear();
        deviceIndex.clear();
    };

    /**
     * Stop the track and remove the track from `streamTrackMap`
     */
    const remove = (track: MediaStreamTrackLike) => {
        const key = toStreamTrackKey(track);
        removeFromDeviceIndex(track);
        track.stop();
        return streamTrackMap.delete(key);
    };

    /**
     * Get the size of `streamTrackMap`
     */
    const size = () => streamTrackMap.size;

    return {
        add,
        has,
        clear,
        remove,
        size,
    };
};

export type StreamTrackMap = ReturnType<typeof createStreamTrackMap>;
